import {AlertTriangle, BookOpen, LockKeyhole, ShieldCheck, Sparkles} from 'lucide-react';
import {Citation, Ticket} from '../../types';
import {TicketReplyController} from '../../hooks/useTicketReply';
import {TicketFeedbackController} from '../../hooks/useTicketFeedback';
import {Spinner} from '../Shared';
import {DraftActions, ResolutionActions} from './DraftActions';
import {DraftFeedbackButtons, DraftFeedbackNote} from './DraftFeedback';
import {AIModelSelector, DraftModelLabel} from '../AIModelSelector';

function groundingLabel(ticket: Ticket) {
  if (!ticket.draft) return 'Ready to draft';
  if (ticket.needs_human) return 'Needs human review';
  const suffix = ticket.citations.length === 1 ? '' : 's';
  return `Grounded in ${ticket.citations.length} source${suffix}`;
}

function saveLabel(dirty: boolean, approved: boolean) {
  if (dirty) return 'Unsaved changes';
  return approved ? 'Approved by you' : 'Review before sharing';
}

const DraftToolbar = ({ticket, reply}: {ticket: Ticket; reply: TicketReplyController}) => {
  const GroundingIcon = ticket.needs_human ? AlertTriangle : ShieldCheck;
  return <div className="draft-toolbar">
    <span className={`grounded-label ${ticket.needs_human ? 'warning' : ''}`} data-testid="draft-grounding-status"><GroundingIcon size={12}/> {groundingLabel(ticket)}</span>
    <div className="draft-controls"><AIModelSelector testId="draft-ai-model-select" disabled={reply.busy}/>
    <select className="draft-tone" value={reply.tone} onChange={event => reply.setTone(event.target.value)} data-testid="draft-tone" aria-label="Reply tone" disabled={reply.busy}>
      <option>Friendly</option><option>Concise</option><option>Formal</option>
    </select></div>
  </div>;
};

const DraftCitations = ({citations}: {citations: Citation[]}) => {
  if (!citations.length) return null;
  return <div className="draft-citations">{citations.map((citation, index) =>
    <button key={citation.id} className="citation-chip" title={citation.excerpt} data-testid={`draft-citation-${index}`}
      onClick={() => document.getElementById(`source-${citation.id}`)?.scrollIntoView({behavior: 'smooth', block: 'center'})}>
      <BookOpen size={10}/>{index + 1}. {citation.title}
    </button>)}
  </div>;
};

type EditorProps = {ticket: Ticket; reply: TicketReplyController; feedback: TicketFeedbackController};
export const DraftEditor = ({ticket, reply, feedback}: EditorProps) => <section className="draft-section">
  <div className="section-heading"><h2><Sparkles size={15}/>Draft reply</h2><DraftModelLabel ticket={ticket} processing={reply.busy}/></div>
  {ticket.ai_error && !reply.busy && <div className="error-notice" role="alert" data-testid="draft-error"><AlertTriangle size={14}/>{ticket.ai_error}</div>}
  <div className="draft-box">
    <DraftToolbar ticket={ticket} reply={reply}/>
    {reply.busy && <div className="generation-status" data-testid="draft-generation-status"><Spinner/>{reply.generation}</div>}
    <textarea className="draft-editor" value={reply.draft} onChange={event => reply.setDraft(event.target.value)} placeholder="Your reply will appear here. You can also write one yourself…" aria-label="Draft reply" data-testid="draft-reply-editor" disabled={reply.busy} maxLength={20000}/>
    <DraftCitations citations={ticket.citations}/>
    <div className="draft-footer">
      <span data-testid="draft-save-state">{saveLabel(reply.dirty, ticket.approved)}</span>
      <DraftFeedbackButtons ticket={ticket} disabled={!reply.draft || reply.busy} feedback={feedback}/>
    </div>
  </div>
  <DraftFeedbackNote feedback={feedback}/>
  <DraftActions ticket={ticket} reply={reply}/>
  <div className="review-note" data-testid="no-send-notice"><LockKeyhole size={10}/>Nothing is sent automatically. You’re always in control.</div>
  <ResolutionActions ticket={ticket} reply={reply}/>
</section>;